import React, { useState, useEffect, useRef } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import "../../assets/css/admin/AdminSidebar.css";

const AdminSidebar = ({ isCollapsed, toggleSidebar }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const bottomProfileRef = useRef(null);

  const buildFullName = () => {
    const first = localStorage.getItem("firstname") || "";
    const last = localStorage.getItem("lastname") || "";
    return (first + " " + last).trim() || "Admin";
  };

  const [userData, setUserData] = useState({
    name: buildFullName(),
    role: "Administrator",
    email: localStorage.getItem("email") || "admin@techmentorhub.com",
    profileImage: null,
  });

  useEffect(() => {
    setUserData((prev) => ({
      ...prev,
      name: buildFullName(),
      email: localStorage.getItem("email") || prev.email,
    }));
    const savedImage = localStorage.getItem("as-profile-image");
    if (savedImage)
      setUserData((prev) => ({ ...prev, profileImage: savedImage }));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [bottomDropdownOpen, setBottomDropdownOpen] = useState(false);

  // ── Earnings sub-menu ───────────────────────────────────────────────────────
  const earningsSubPaths = ["/admin/earnings", "/admin/withdrawals"];
  const [earningsOpen, setEarningsOpen] = useState(() =>
    earningsSubPaths.some((p) => location.pathname.startsWith(p)),
  );

  useEffect(() => {
    if (earningsSubPaths.some((p) => location.pathname.startsWith(p))) {
      setEarningsOpen(true);
    }
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        bottomProfileRef.current &&
        !bottomProfileRef.current.contains(event.target)
      )
        setBottomDropdownOpen(false);
    };
    if (bottomDropdownOpen)
      document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [bottomDropdownOpen]);

  const handlePhotoUpload = (event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith("image/")) {
      if (file.size > 5 * 1024 * 1024) {
        alert("Image size should be less than 5MB");
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        localStorage.setItem("as-profile-image", reader.result);
        setUserData((prev) => ({ ...prev, profileImage: reader.result }));
      };
      reader.onerror = () => alert("Failed to read image file");
      reader.readAsDataURL(file);
    } else if (file) {
      alert("Please select a valid image file");
    }
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (cameraInputRef.current) cameraInputRef.current.value = "";
  };

  const handleLogout = () => {
    navigate("/admin/login");
    setBottomDropdownOpen(false);
  };
  const getUserInitial = () => userData.name.charAt(0).toUpperCase();

  const menuItems = [
    {
      name: "Dashboard",
      path: "/admin/dashboard",
      icon: (
        <svg viewBox="0 0 24 24" fill="none">
          <rect
            x="3"
            y="3"
            width="7"
            height="7"
            rx="1"
            stroke="currentColor"
            strokeWidth="2"
          />
          <rect
            x="14"
            y="3"
            width="7"
            height="7"
            rx="1"
            stroke="currentColor"
            strokeWidth="2"
          />
          <rect
            x="3"
            y="14"
            width="7"
            height="7"
            rx="1"
            stroke="currentColor"
            strokeWidth="2"
          />
          <rect
            x="14"
            y="14"
            width="7"
            height="7"
            rx="1"
            stroke="currentColor"
            strokeWidth="2"
          />
        </svg>
      ),
    },
    {
      name: "Sub Admin ",
      path: "/admin/sub-admin",
      icon: (
        <svg viewBox="0 0 24 24" fill="none">
          <path
            d="M12 15C15.866 15 19 11.866 19 8C19 4.134 15.866 1 12 1C8.134 1 5 4.134 5 8C5 11.866 8.134 15 12 15Z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M8.21 13.89L7 23L12 20L17 23L15.79 13.88"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <circle cx="12" cy="8" r="2" stroke="currentColor" strokeWidth="2" />
        </svg>
      ),
    },

    // ── Students Management ──────────────────────────────────────────────────
    {
      name: "Students Management",
      path: "/admin/students",
      icon: (
        <svg viewBox="0 0 24 24" fill="none">
          <path
            d="M12 3L1 9L12 15L21 10.09V17H23V9L12 3Z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M5 13.18V17.18L12 21L19 17.18V13.18L12 17L5 13.18Z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
    },

    // ── Mentor Management ────────────────────────────────────────────────────
    {
      name: "Mentors Management",
      path: "/admin/mentors",
      icon: (
        <svg viewBox="0 0 24 24" fill="none">
          <path
            d="M17 21V19C17 16.791 15.209 15 13 15H5C2.791 15 1 16.791 1 19V21"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="2" />
          <path
            d="M23 21V19C22.999 18.114 22.704 17.253 22.161 16.552C21.618 15.852 20.858 15.352 20 15.13"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M16 3.13C16.86 3.35 17.623 3.851 18.168 4.552C18.712 5.254 19.008 6.117 19.008 7.005C19.008 7.893 18.712 8.756 18.168 9.458C17.623 10.159 16.86 10.66 16 10.88"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M20 8L22 10L20 12"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
    },

    {
      name: "Categories",
      path: "/admin/categories",
      icon: (
        <svg viewBox="0 0 24 24" fill="none">
          <path
            d="M3 5C3 3.895 3.895 3 5 3H9.586C9.851 3 10.105 3.105 10.293 3.293L11.707 4.707C11.895 4.895 12.149 5 12.414 5H19C20.105 5 21 5.895 21 7V19C21 20.105 20.105 21 19 21H5C3.895 21 3 20.105 3 19V5Z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M8 12H16M8 16H13"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
    },
    {
      name: "Courses",
      path: "/admin/courses",
      icon: (
        <svg viewBox="0 0 24 24" fill="none">
          <path
            d="M2 3H8C9.06 3 10.078 3.421 10.828 4.172C11.579 4.922 12 5.939 12 7V21C12 20.204 11.684 19.441 11.121 18.879C10.559 18.316 9.796 18 9 18H2V3Z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M22 3H16C14.939 3 13.922 3.421 13.172 4.172C12.421 4.922 12 5.939 12 7V21C12 20.204 12.316 19.441 12.879 18.879C13.441 18.316 14.204 18 15 18H22V3Z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
    },
    {
      name: "Roadmaps Management",
      path: "/admin/roadmaps",
      icon: (
        <svg viewBox="0 0 24 24" fill="none">
          <path
            d="M22 11.08V12C21.999 14.156 21.3 16.255 20.009 17.982C18.718 19.709 16.903 20.972 14.835 21.584C12.767 22.195 10.557 22.122 8.534 21.375C6.512 20.627 4.785 19.246 3.611 17.437C2.437 15.628 1.88 13.488 2.022 11.336C2.164 9.185 2.997 7.136 4.398 5.497C5.799 3.858 7.693 2.715 9.796 2.24C11.9 1.765 14.1 1.982 16.07 2.86"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M22 4L12 14.01L9 11.01"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
    },
    {
      name: "Mentors Verification",
      path: "/admin/mentors/verification",
      icon: (
        <svg viewBox="0 0 24 24" fill="none">
          <path
            d="M16 21V19C16 16.791 14.209 15 12 15H5C2.791 15 1 16.791 1 19V21"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <circle cx="8.5" cy="7" r="4" stroke="currentColor" strokeWidth="2" />
          <path
            d="M20 8V14"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M23 11L20 14L17 11"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
    },

    // ── EARNINGS MANAGEMENT ───────────────────────────────────────────────────
    {
      name: "Earnings Management",
      path: "/admin/earnings",
      hasChildren: true,
      icon: (
        <svg viewBox="0 0 24 24" fill="none">
          <line
            x1="12"
            y1="1"
            x2="12"
            y2="23"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
          <path
            d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
      children: [
        {
          name: "Overview",
          path: "/admin/earnings",
          icon: (
            <svg viewBox="0 0 24 24" fill="none" width="14" height="14">
              <rect
                x="3"
                y="3"
                width="7"
                height="7"
                rx="1"
                stroke="currentColor"
                strokeWidth="2"
              />
              <rect
                x="14"
                y="3"
                width="7"
                height="4"
                rx="1"
                stroke="currentColor"
                strokeWidth="2"
              />
              <rect
                x="3"
                y="14"
                width="7"
                height="7"
                rx="1"
                stroke="currentColor"
                strokeWidth="2"
              />
              <rect
                x="14"
                y="11"
                width="7"
                height="10"
                rx="1"
                stroke="currentColor"
                strokeWidth="2"
              />
            </svg>
          ),
        },
        {
          name: "Withdrawals Request",
          path: "/admin/withdrawals",
          icon: (
            <svg viewBox="0 0 24 24" fill="none" width="14" height="14">
              <path
                d="M20 7H4C2.895 7 2 7.895 2 9V19C2 20.105 2.895 21 4 21H20C21.105 21 22 20.105 22 19V9C22 7.895 21.105 7 20 7Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M16 3L20 7H4L8 3H16Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <circle cx="17" cy="14" r="1.5" fill="currentColor" />
            </svg>
          ),
        },
      ],
    },

    {
      name: "AI Tutor",
      path: "/admin/ai-tutor",
      icon: (
        <svg viewBox="0 0 24 24" fill="none">
          <rect
            x="4"
            y="4"
            width="16"
            height="16"
            rx="4"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <circle cx="9" cy="10" r="1.5" fill="currentColor" />
          <circle cx="15" cy="10" r="1.5" fill="currentColor" />
          <path
            d="M9 15C9 15 10 16.5 12 16.5C14 16.5 15 15 15 15"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M4 8H2M22 8H20M4 16H2M22 16H20"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
    },
    {
      name: "Analytics",
      path: "/admin/analytics",
      icon: (
        <svg viewBox="0 0 24 24" fill="none">
          <path
            d="M18 20V10"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M12 20V4"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M6 20V14"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
    },
    {
      name: "Reports & Disputes",
      path: "/admin/reports",
      icon: (
        <svg viewBox="0 0 24 24" fill="none">
          <path
            d="M14 2H6C5.47 2 4.961 2.211 4.586 2.586C4.211 2.961 4 3.47 4 4V20C4 20.53 4.211 21.039 4.586 21.414C4.961 21.789 5.47 22 6 22H18C18.53 22 19.039 21.789 19.414 21.414C19.789 21.039 20 20.53 20 20V8L14 2Z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M14 2V8H20M12 18V12M9 15H15"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
    },
    {
      name: "System Settings",
      path: "/admin/settings",
      icon: (
        <svg viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" />
          <path
            d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
    },
  ];

  return (
    <aside className={`as-sidebar ${isCollapsed ? "as-collapsed" : ""}`}>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handlePhotoUpload}
        style={{ display: "none" }}
        aria-label="Upload profile photo"
      />
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handlePhotoUpload}
        style={{ display: "none" }}
        aria-label="Take photo"
      />

      {/* Brand */}
      <div className="as-brand-header">
        <div className="as-brand-logo">
          <span className="as-brand-logo-text">TMH</span>
        </div>
        {!isCollapsed && (
          <div className="as-brand-info">
            <span className="as-brand-name">Techmentor</span>
            <span className="as-brand-sub">Hub</span>
          </div>
        )}
      </div>

      {/* Toggle */}
      <button
        className="as-toggle-btn"
        onClick={toggleSidebar}
        aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        title={isCollapsed ? "Expand" : "Collapse"}
      >
        <svg
          className={`as-toggle-icon ${isCollapsed ? "as-toggle-icon--collapsed" : ""}`}
          viewBox="0 0 24 24"
          fill="none"
        >
          <path
            d="M15 18L9 12L15 6"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      <div className="as-divider as-divider--brand" />

      {/* Nav */}
      <nav className="as-nav">
        <ul className="as-menu-list">
          {menuItems.map((item) => (
            <li key={item.path} className="as-menu-item">
              {item.hasChildren ? (
                <>
                  <div
                    className={`as-menu-link as-menu-link--parent ${
                      earningsSubPaths.some((p) =>
                        location.pathname.startsWith(p),
                      )
                        ? "as-active"
                        : ""
                    }`}
                    onClick={() => {
                      if (!isCollapsed) setEarningsOpen((o) => !o);
                      navigate(item.path);
                    }}
                    title={isCollapsed ? item.name : ""}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === "Enter" && navigate(item.path)}
                    style={{ cursor: "pointer" }}
                  >
                    <span className="as-menu-icon">{item.icon}</span>
                    {!isCollapsed && (
                      <>
                        <span className="as-menu-text">{item.name}</span>
                        <svg
                          className={`as-submenu-chevron ${earningsOpen ? "as-submenu-chevron--open" : ""}`}
                          viewBox="0 0 24 24"
                          fill="none"
                          width="14"
                          height="14"
                        >
                          <path
                            d="M6 9L12 15L18 9"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </>
                    )}
                  </div>

                  {!isCollapsed && earningsOpen && (
                    <ul className="as-submenu-list">
                      {item.children.map((child) => (
                        <li key={child.path}>
                          <NavLink
                            to={child.path}
                            end
                            className={({ isActive }) =>
                              `as-submenu-link ${isActive ? "as-active" : ""}`
                            }
                          >
                            <span className="as-submenu-icon">
                              {child.icon}
                            </span>
                            <span className="as-menu-text">{child.name}</span>
                          </NavLink>
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              ) : (
                <NavLink
                  to={item.path}
                  className={({ isActive }) =>
                    `as-menu-link ${isActive ? "as-active" : ""}`
                  }
                  title={isCollapsed ? item.name : ""}
                >
                  <span className="as-menu-icon">{item.icon}</span>
                  {!isCollapsed && (
                    <span className="as-menu-text">{item.name}</span>
                  )}
                </NavLink>
              )}
            </li>
          ))}
        </ul>
      </nav>

      {/* Bottom profile */}
      <div className="as-bottom-profile" ref={bottomProfileRef}>
        <div className="as-divider as-divider--bottom" />
        <div
          className={`as-bottom-profile-inner ${isCollapsed ? "as-bottom-profile-inner--collapsed" : ""}`}
        >
          <div className="as-bottom-avatar">
            {userData.profileImage ? (
              <img
                src={userData.profileImage}
                alt={userData.name}
                className="as-bottom-avatar-image"
              />
            ) : (
              <div className="as-bottom-avatar-letter">{getUserInitial()}</div>
            )}
          </div>
          {!isCollapsed && (
            <div className="as-bottom-user-info">
              <p className="as-bottom-user-name">{userData.name}</p>
              <p className="as-bottom-user-email">{userData.email}</p>
            </div>
          )}
          {!isCollapsed && (
            <button
              className="as-dots-btn"
              onClick={() => setBottomDropdownOpen(!bottomDropdownOpen)}
              aria-label="User options"
            >
              <svg
                viewBox="0 0 24 24"
                fill="currentColor"
                width="18"
                height="18"
              >
                <circle cx="12" cy="5" r="1.5" />
                <circle cx="12" cy="12" r="1.5" />
                <circle cx="12" cy="19" r="1.5" />
              </svg>
            </button>
          )}
        </div>
        {bottomDropdownOpen && !isCollapsed && (
          <div className="as-bottom-dropdown">
            <button
              className="as-bottom-dropdown-item as-bottom-dropdown-item--logout"
              onClick={handleLogout}
            >
              <svg viewBox="0 0 24 24" fill="none" width="16" height="16">
                <path
                  d="M9 21H5C4.47 21 3.961 20.789 3.586 20.414C3.211 20.039 3 19.53 3 19V5C3 4.47 3.211 3.961 3.586 3.586C3.961 3.211 4.47 3 5 3H9"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M16 17L21 12L16 7M21 12H9"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span>Logout</span>
            </button>
          </div>
        )}
      </div>
    </aside>
  );
};

export default AdminSidebar;
